import { GET_DEFAULT_GROUPS, GET_PARTNER_CLIENT_ACCOUNT_EMPLOYEES_NEW } from '@/common/actions/get';
import { useDebouncedEffect } from '@/common/hooks/useDebouncedEffect';
import { COLORS, OrderTypes } from '@/constants';
import { formatUserFullName } from '../../store';
import { useLazyQuery } from '@apollo/client';
import { Box, Button, Checkbox, Input, Modal, Typography } from '@servicepipe/ui-components';
import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';

const TableWrapper = styled.div`
  display: flex;
  flex-direction: column;
  border: 1px solid ${COLORS.grayDivider};
  border-radius: 4px;
  overflow: hidden;
`;

const TableHeader = styled.div`
  display: grid;
  grid-template-columns: 48px 305px 1fr;
  gap: 8px;
  padding: 8px 12px;
  background: ${COLORS.grayLight};
  border-bottom: 1px solid ${COLORS.grayDivider};
  align-items: center;
`;

const TableBody = styled.div`
  max-height: min(200px, calc(60vh - 280px));
  overflow-y: auto;
`;

const TableRow = styled.div`
  display: grid;
  grid-template-columns: 48px 305px 1fr;
  gap: 8px;
  padding: 8px 12px;
  align-items: center;
  border-bottom: 1px solid ${COLORS.grayDivider};
  background: ${({ $selected, $disabled }) =>
    $selected && !$disabled
      ? 'rgba(0, 123, 255, 0.08)'
      : $disabled
        ? COLORS.grayLight
        : COLORS.whiteHighEmphasis};
  color: ${({ $disabled }) => ($disabled ? COLORS.grayDark : COLORS.blackHighEmphasis)};

  &:last-child {
    border-bottom: none;
  }
`;

export const ModalAddAdministrators = ({ onToggle, addAdminHandler }) => {
  const { id } = useParams();
  const [emailFilter, setEmailFilter] = useState('');
  const [users, setUsers] = useState([]);

  const [getDefaultGroupsData, { data: defaultGroups }] = useLazyQuery(GET_DEFAULT_GROUPS);
  const [getEmployees, { data, loading }] = useLazyQuery(GET_PARTNER_CLIENT_ACCOUNT_EMPLOYEES_NEW, {
    fetchPolicy: 'network-only',
  });

  useEffect(() => {
    getDefaultGroupsData({
      variables: {
        page: 1,
        limit: 3000,
        deletedAt: null,
        partnerClientAccountId: Number(id),
      },
    });
  }, [id]);

  useDebouncedEffect(
    () => {
      getEmployees({
        variables: {
          page: 1,
          pageSize: 3000,
          deletedAt: null,
          partnerClientAccountId: [Number(id)],
          filterEmail: emailFilter || undefined,
          sortId: OrderTypes.Desc,
        },
      });
    },
    300,
    [id, emailFilter]
  );

  useEffect(() => {
    const adminIds = new Set(
      (defaultGroups?.defaultGroups?.items || []).map((item) => item.partnerClientAccountEmployeeId)
    );

    setUsers(
      (data?.partnerClientAccountEmployees?.items || []).map((item) => ({
        ...item,
        isSelected: false,
        isAdminDefault: adminIds.has(item.id) || Boolean(item.isInDefaultGroup),
      }))
    );
  }, [data, defaultGroups]);

  const selectedIds = useMemo(
    () => users.filter((item) => item.isSelected).map((item) => item.id),
    [users]
  );

  const availableIds = useMemo(
    () => users.filter((item) => !item.isAdminDefault).map((item) => item.id),
    [users]
  );

  const allSelected =
    availableIds.length > 0 && availableIds.every((userId) => selectedIds.includes(userId));

  return (
    <Modal
      enableMaximize={false}
      size="m"
      title="Add administrator"
      toggle={onToggle}
      footer={
        <Box display="flex" justify="flex-end" gap="8px" width="100%" padding="0 8px">
          <Button variant="outline" color="neutral" type="button" onClick={onToggle}>
            Cancel
          </Button>
          <Button
            variant="solid"
            color="accent"
            type="button"
            isDisabled={!selectedIds.length || loading}
            onClick={() => addAdminHandler(selectedIds)}
          >
            Add
          </Button>
        </Box>
      }
    >
      <Box direction="column" gap="16px" width="100%">
        <Typography variant="h6">All users</Typography>

        <Input
          width="100%"
          name="email"
          value={emailFilter}
          onChange={(event) => setEmailFilter(event.target.value)}
          placeholder="Email"
        />

        <TableWrapper>
          <TableHeader>
            <Box align="center" justify="center">
              <Checkbox
                checked={allSelected}
                disabled={availableIds.length === 0 || loading}
                onChange={() => {
                  const shouldSelect = !allSelected;
                  setUsers((prev) =>
                    prev.map((item) =>
                      item.isAdminDefault ? item : { ...item, isSelected: shouldSelect }
                    )
                  );
                }}
              />
            </Box>
            <Typography variant="subtitle2">Email</Typography>
            <Typography variant="subtitle2">Name</Typography>
          </TableHeader>

          <TableBody>
            {loading && (
              <Box padding="16px">
                <Typography variant="body2">Loading resources...</Typography>
              </Box>
            )}

            {!loading && !users.length && (
              <Box padding="16px">
                <Typography variant="body2">No resources found.</Typography>
              </Box>
            )}

            {!loading &&
              users.map((user) => (
                <TableRow key={user.id} $disabled={user.isAdminDefault} $selected={user.isSelected}>
                  <Box align="center" justify="center">
                    <Checkbox
                      checked={user.isAdminDefault || user.isSelected}
                      disabled={user.isAdminDefault}
                      onChange={() =>
                        setUsers((prev) =>
                          prev.map((item) =>
                            item.id === user.id ? { ...item, isSelected: !item.isSelected } : item
                          )
                        )
                      }
                    />
                  </Box>
                  <Typography variant="body2">{user.email}</Typography>
                  <Typography variant="body2">{formatUserFullName(user.name) || '-'}</Typography>
                </TableRow>
              ))}
          </TableBody>
        </TableWrapper>
      </Box>
    </Modal>
  );
};
